import extend from 'extend';
import mapScopeItem from '../../components/RepairSidebar/lib/mapScopeItem';
import mapFinalRepair from '../../components/RepairSidebar/lib/mapFinalRepairItem';
import * as repairsUtils from '../../lib/repairs';
import getPrevSelectedItemCount from '../../lib/getPrevSelectedItemCount';
import { toggleMaxItemsReached } from '../../actions/restrictions';
import { returnItemsService } from '../../services/return-items-service/return-items-service';
import { store } from '../../store';

const repairSidebarService = () => {
  const {
    language,
    page: { productType },
    returnsSettings: { [productType]: { repairsConfig, sidebar_item_attrs: sidebarAttrs } = {} },
  } = store.getState();
  const node = document.createElement('div');
  const maxCommentLength = 300;
  document.body.appendChild(node);
  let updateCallback = null;
  let customerFileUpload;

  let sidebarState = {
    config: repairsConfig,
    open: false,
    loading: true,
    currentItem: null,
    panel: 0,
    repairReasonCollection: {},
    selectedReason: null,
    temporaryItems: {},
    savedItems: {},
  };

  const updateState = (newState, deep = true) => {
    sidebarState = extend(deep, sidebarState, newState);
    renderSidebar();
  };

  const actions = {
    initialize: (item) => updateState(initializeSidebar(item), false),

    toggle: (overrideSave, isFinal) => updateState(toggleSidebar(overrideSave, isFinal)),

    toggleLoad: (loading) => updateState({ loading }),

    switchPanel: (panel) => updateState({ panel }),

    addInputKey: (inputKey) =>
      updateState({ currentItem: { ...sidebarState.currentItem, [inputKey]: null } }, false),

    addRequiredField: (fieldName) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            requiredFields: [...(sidebarState.currentItem.requiredFields || []), fieldName],
          },
        },
        false,
      );
    },

    formChange: (data) => updateState({ currentItem: { ...updateForm(data) } }),

    updateReasons: (repairReasons) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            repairReasons: addCostToNames(repairReasons),
          },
        },
        false,
      );
    },

    updateReasonCollection: (repairReasonCollection) => updateState({ repairReasonCollection }),

    selectReason: (reasonIndex, reason, multipleSelect) => {
      updateState(chooseReason(reasonIndex, reason, multipleSelect), false);
    },

    resetReason: (reasonIndex, reason) => {
      updateState({ currentItem: deselectReason(reasonIndex, reason) }, false);
    },

    saveItemProgress: () => updateState(saveProgress(), false),

    finalizeItem: async (additionalItem) =>
      updateState(await finalizeRepairItem(additionalItem), false),

    deleteItem: () => updateState(deleteItem(), false),

    resetSidebar: () => updateState({ open: false }),

    setLastEnabledSection: (sectionIndex) => {
      updateState(
        {
          currentItem: {
            ...sidebarState.currentItem,
            lastEnabledSection: sectionIndex,
          },
        },
        false,
      );
    },

    deletePhoto: (inputKey) => {
      const img = document.getElementById('imageThumbnail');
      const fileInput = document.getElementById('fileInput');

      if (img && fileInput) {
        img.file = null;
        img.src = '/r2/img/icon_loading.svg';
        fileInput.value = null;
      }

      if (sidebarState.currentItem.item_images && sidebarState.currentItem.item_images[0]) {
        repairsUtils
          .deleteImage(store)(sidebarState.currentItem.item_images[0])
          .then(
            () => {},
            () => {},
          );
        sidebarState.currentItem.item_images.pop();
      }

      return updateState({
        currentItem: {
          ...sidebarState.currentItem,
          item_images: [],
          repairImageUrl: '',
          imageUploadErrorTooltip: '',
          showRepairImage: false,
          isUploadingImage: false,
          [inputKey]: false,
        },
      });
    },

    uploadFile: (inputKey) => {
      const file = customerFileUpload.files[0];

      if (file.size > 10485760) {
        return updateState({
          currentItem: {
            imageUploadErrorTooltip: language['returns.step_1.sidepanel_file_size_error_tooltip'],
          },
        });
      }

      return uploadFileData(file, inputKey);
    },

    resetImageErrorMsg: () => {
      updateState({
        currentItem: {
          ...sidebarState.currentItem,
          imageUploadErrorTooltip: '',
        },
      });
    },
  };

  const retakePhoto = () => {
    document.getElementById('fileInput').click();
  };

  const uploadFileData = async (file, inputKey) => {
    const img = document.getElementById('imageThumbnail');
    img.file = file;
    img.src = '/r2/img/icon_loading.svg';

    updateState({
      currentItem: {
        showRepairImage: true,
        isUploadingImage: true,
      },
    });

    try {
      const { filename, path } = await repairsUtils.uploadImage(store)(file);
      return updateState({
        currentItem: {
          item_images: [filename],
          repairImageUrl: `${path}${filename}`,
          imageUploadErrorTooltip: '',
          isUploadingImage: false,
          [inputKey]: true,
        },
      });
    } catch (error) {
      const repairImageUrl = sidebarState.currentItem.repairImageUrl;
      if (repairImageUrl) {
        img.src = repairImageUrl;
      }
      return updateState({
        currentItem: {
          imageUploadErrorTooltip: language['returns.step_1.sidepanel_image_upload_error_tooltip'],
          showRepairImage: Boolean(repairImageUrl),
          isUploadingImage: false,
        },
      });
    }
  };

  const getRepairReasons = (reasonCategory, itemProperties) => {
    const { repairReasonCollection } = sidebarState;

    repairsUtils
      .getReasonHierarchy(store)(reasonCategory, itemProperties)
      .then((data) => {
        const { response } = data;
        actions.updateReasons(response);

        if (!repairReasonCollection[reasonCategory]) {
          actions.updateReasonCollection({ [reasonCategory]: response });
        }

        actions.toggleLoad(false);
      })
      .catch((error) => console.error(error));
  };

  const initializeSidebar = (item) => {
    const mappedScopeItem = { ...mapScopeItem(item), ...item };
    const { itemId, itemProperties, itemQuantity, itemCategory } = mappedScopeItem;
    const { temporaryItems } = sidebarState;
    const itemInProgress = temporaryItems[itemId];

    const update = {
      currentItem: itemInProgress || mappedScopeItem,
      itemProperties,
      itemQuantity,
      temporaryItems,
    };

    if (!update.currentItem.repairReasons) {
      update.temporaryItems[itemId] = { ...mappedScopeItem };
      getRepairReasons(itemCategory, itemProperties);
    }

    return update;
  };

  const toggleSidebar = (overrideSave, isFinal) => {
    const {
      restrictions: { maxItemsReached },
    } = store.getState();

    if (sidebarState.open && maxItemsReached && !isFinal) {
      return { open: true };
    }

    if (sidebarState.open && !overrideSave) {
      actions.saveItemProgress();
    }

    return { open: !sidebarState.open };
  };

  const saveProgress = () => {
    const { currentItem, temporaryItems } = sidebarState;
    const { itemId } = currentItem;
    return { temporaryItems: { ...temporaryItems, [itemId]: currentItem } };
  };

  const setUpdateCallback = (callback) => {
    updateCallback = callback;
  };

  const buildFormState = (formComponent, sectionId) => {
    const { inputKey, required } = formComponent;
    const {
      currentItem: { requiredFields = [], dynamicSections },
    } = sidebarState;
    const isNewRequiredField = required && requiredFields.indexOf(inputKey) < 0;

    let isActiveSection = true;
    if (dynamicSections && !dynamicSections.includes(sectionId)) {
      isActiveSection = false;
    }

    if (isNewRequiredField && isActiveSection) {
      actions.addRequiredField(inputKey);
    }
  };

  const checkDisabledSection = (requiredInput, sectionIndex, id) => {
    const { currentItem } = sidebarState;
    const { lastEnabledSection, dynamicSections } = currentItem;
    const storedValue = currentItem[requiredInput];
    const emptyMultiSelect = Array.isArray(storedValue) && !storedValue.length;
    const emptyRequiredField = !storedValue || emptyMultiSelect;
    const visible = dynamicSections ? dynamicSections.includes(id) : true;

    if (emptyRequiredField && lastEnabledSection === null && visible) {
      actions.setLastEnabledSection(sectionIndex);
    }
  };

  const getReasonIdPath = (repairReasonPath, isFinalReasons) =>
    repairReasonPath.reduce((finalReasons, step) => {
      if (Array.isArray(step)) {
        const multiSelectReasonIds = step.map((substep) => substep.reason_code_id);
        finalReasons.push(multiSelectReasonIds);
      } else if (step.reason_code_id) {
        const reasonId = isFinalReasons ? [step.reason_code_id] : step.reason_code_id;
        finalReasons.push(reasonId);
      }
      return finalReasons;
    }, []);

  const mapFinalReasons = () => {
    const { currentItem } = sidebarState;
    const { requiredFields = [] } = currentItem;
    const chosenReasons = requiredFields.map((field) => currentItem[field]);
    return getReasonIdPath(chosenReasons, true);
  };

  const getReasonCost = (reason) => {
    const reasonSettings = reason.reason_settings || {};
    const reasonAttributes = reasonSettings.attributes || {};
    const reasonCost = reasonAttributes.cost;
    return reasonCost && parseInt(reasonCost, 10);
  };

  const getDynamicSections = (reason = {}) => {
    const reasonSettings = reason.reason_settings || {};
    const reasonAttributes = reasonSettings.attributes || {};
    const sectionsStr = reasonAttributes.sections;

    if (!sectionsStr) {
      return null;
    }

    const sections = sectionsStr.split(',').map((id) => +id);
    return sections;
  };

  const addCostToNames = (reasons = []) =>
    reasons.map((reason) => {
      const reasonCost = getReasonCost(reason);
      let costDisplay = reasonCost === 0 ? 'Free' : reasonCost;

      if (costDisplay === 0 || costDisplay !== undefined) {
        costDisplay = costDisplay === 'Free' ? costDisplay : `$${costDisplay}`;
        return { ...reason, display_name: `${reason.display_name} (${costDisplay})` };
      }

      return reason;
    });

  const reasonSelected = (reason, propName, formKey) => {
    const { currentItem } = sidebarState;
    let selectedReason = currentItem[formKey];
    const multipleReasons = Array.isArray(selectedReason);

    if (multipleReasons) {
      selectedReason = getReasonIdPath(selectedReason);
      return selectedReason.indexOf(reason.reason_code_id) > -1;
    }

    return selectedReason;
  };

  // TODO: Populate multipleSelectReasons before the first multi select reason is selected.
  const chooseReason = (reasonIndex, inputKey, multipleSelect) => {
    const { currentItem } = sidebarState;
    const { reasonPath, repairReasons, dynamicSections, multipleSelectReasons = [] } = currentItem;
    const previousSelection = currentItem[inputKey] || [];
    let selectedReason = { ...repairReasons[reasonIndex] };
    let nextLevelReasons = selectedReason.reasons;
    let newMultipleSelectReasons = multipleSelectReasons;

    if (multipleSelect) {
      if (multipleSelectReasons.length > 0) {
        selectedReason = { ...multipleSelectReasons[reasonIndex] };
        nextLevelReasons = repairReasons;
      } else {
        newMultipleSelectReasons = repairReasons;
      }
    }

    const reasonCost = getReasonCost(selectedReason);
    const newDynamicSections = getDynamicSections(selectedReason) || dynamicSections;

    const update = {
      currentItem: {
        ...currentItem,
        [inputKey]: multipleSelect ? [...previousSelection, selectedReason] : selectedReason,
        reasonPath: [...reasonPath, selectedReason],
        repairReasons: nextLevelReasons ? addCostToNames(nextLevelReasons) : repairReasons,
        multipleSelectReasons: newMultipleSelectReasons,
        lastEnabledSection: null,
      },
    };

    if (reasonCost) {
      update.currentItem.repairCost = reasonCost;
    }

    if (newDynamicSections) {
      update.currentItem.requiredFields = [];
      update.currentItem.dynamicSections = newDynamicSections;
    }

    return update;
  };

  const deselectReason = (reasonIndex, inputKey) => {
    const { currentItem, repairReasonCollection } = sidebarState;
    const { itemCategory, reasonPath, requiredFields, multipleSelectReasons } = currentItem;
    const selection = currentItem[inputKey];
    const multipleReasons = Array.isArray(selection);
    const deleteFields = requiredFields.slice(requiredFields.indexOf(inputKey));
    let endPathTrace = false;
    let newReasonSelections;
    let newReasonTier = [...repairReasonCollection[itemCategory]];
    let newPathEndIndex = 0;
    let newReasonPath = reasonPath;
    let newMultipleSelectReasons = multipleSelectReasons;

    actions.deletePhoto();

    deleteFields.forEach((field) => {
      if (!multipleReasons && Array.isArray(currentItem[field])) {
        newMultipleSelectReasons = [];
      }
      delete currentItem[field];
    });

    if (!multipleReasons) {
      const selectedReasonId = selection.reason_code_id;
      reasonPath.forEach((step, stepIndex) => {
        if (!endPathTrace) {
          const stepId = step.reason_code_id;
          endPathTrace = stepId === selectedReasonId;

          if (!endPathTrace) {
            const nodeMatch = newReasonTier.filter((reason) => {
              const reasonId = reason.reason_code_id;
              const pathEndFound = reasonId === stepId;

              if (pathEndFound) {
                newPathEndIndex = stepIndex + 1;
              }

              return pathEndFound;
            })[0];

            const nextLevelReasons = nodeMatch ? nodeMatch.reasons : [];
            newReasonTier = nextLevelReasons.length > 0 ? nextLevelReasons : newReasonTier;
          }
        }
      });

      newReasonPath = reasonPath.slice(0, newPathEndIndex);
    } else {
      const { reason_code_id: clickedReasonId, reasons } = multipleSelectReasons[reasonIndex];
      newReasonSelections = selection.filter((reason) => reason.reason_code_id !== clickedReasonId);
      newReasonTier = reasons;
    }

    const previousDynamicReason = [...newReasonPath].reverse().find(getDynamicSections);
    return {
      ...currentItem,
      dynamicSections: getDynamicSections(previousDynamicReason),
      repairReasons: addCostToNames(newReasonTier),
      reasonPath: newReasonPath,
      lastEnabledSection: null,
      repairCost: 0,
      [inputKey]: newReasonSelections || currentItem[inputKey],
      multipleSelectReasons: newMultipleSelectReasons,
    };
  };

  const finalizeRepairItem = async (additionalItem) => {
    const {
      restrictions: { itemLimit },
    } = store.getState();
    const { currentItem, savedItems } = sidebarState;
    const { itemId: currentItemId } = currentItem;
    const mappedReasonPath = { repair_reason_code_ids: mapFinalReasons() };

    const finalRepairItem = {
      ...currentItem,
      ...mapFinalRepair(currentItem),
      ...mappedReasonPath,
    };

    returnItemsService.removeItemById(currentItem);
    returnItemsService.addItem(finalRepairItem);
    updateCallback(finalRepairItem, additionalItem);
    actions.toggle(false, true);

    const prevSelectedItemCount = getPrevSelectedItemCount(savedItems, currentItemId);
    const maxItemsReached = prevSelectedItemCount + 1 === itemLimit;
    if (maxItemsReached) {
      store.dispatch(toggleMaxItemsReached(maxItemsReached));
    }

    return { savedItems: { ...savedItems, [currentItemId]: finalRepairItem } };
  };

  const deleteItem = () => {
    const {
      restrictions: { itemLimit },
    } = store.getState();
    const { currentItem, temporaryItems, savedItems } = sidebarState;
    currentItem.selected = false;
    returnItemsService.removeItemById(currentItem);
    delete savedItems[currentItem.itemId];
    delete temporaryItems[currentItem.itemId];

    const newItemCount = Object.keys(savedItems).length;
    const maxItemsReached = newItemCount === itemLimit;
    store.dispatch(toggleMaxItemsReached(maxItemsReached));

    updateCallback(currentItem, true);
    actions.toggle(true);
    return { savedItems, temporaryItems };
  };

  const buttonMenuItems = (inputKey, multipleSelect) => {
    const { currentItem } = sidebarState;
    const { repairReasons, multipleSelectReasons = [] } = currentItem;
    const selectedItem = currentItem[inputKey] && [currentItem[inputKey]];

    if (multipleSelect && multipleSelectReasons.length > 0) {
      return multipleSelectReasons;
    }

    return multipleSelect || !selectedItem ? repairReasons : selectedItem;
  };

  const updateForm = (inputData) => {
    const { name, value } = inputData.target;
    return { [name]: value };
  };

  const checkRequiredFields = () => {
    const currentItem = sidebarState.currentItem || {};
    const requiredFields = currentItem.requiredFields || [];

    if (currentItem.isUploadingImage) {
      return true;
    }

    return requiredFields.reduce((accumulator, current) => {
      const requiredValue = currentItem[current];
      const emptyValue = Array.isArray(requiredValue) && requiredValue.length === 0;
      const invalidValue = !requiredValue || emptyValue;
      return invalidValue || accumulator;
    }, false);
  };

  const checkCommentField = () => {
    const currentItem = sidebarState.currentItem || {};
    const requiredFields = currentItem.requiredFields || [];
    let commentMandatory;

    requiredFields.forEach((field) => {
      const fieldData = currentItem[field] || {};

      if (fieldData.reason_code_id) {
        const reasonSettings = fieldData.reason_settings || {};
        const mandatoryFields = reasonSettings.mandatory_fields || [];
        commentMandatory = mandatoryFields.indexOf('comments') >= 0;
      }
    });

    return commentMandatory && !currentItem.itemComment;
  };

  const renderSidebar = async () => {
    const module = await import(
      /* webpackChunkName: "repair-sidebar" */ `../../components/RepairSidebar/repairSidebar`
    );
    module.renderSidebar({
      actions,
      checkRequiredFields,
      buttonMenuItems,
      checkCommentField,
      reasonSelected,
      buildFormState,
      checkDisabledSection,
      retakePhoto,
      maxCommentLength,
      sidebarAttrs,
      customerFileUpload,
      sidebarState,
      node,
    });
  };

  return {
    actions,
    setUpdateCallback,
  };
};

export const repairSidebar = repairSidebarService();
